/**
 * useWorkoutSession — Active Workout State Machine
 *
 * States:
 *   idle       → no workout active, show Generate button
 *   generating → fetching DB data, running generator
 *   active     → user is training; sets being logged
 *   complete   → session saved, split day advanced
 *   abandoned  → session discarded (auto-returns to idle)
 *
 * Timer is anchored to an absolute start timestamp so background-tab
 * throttling never causes drift — visibilitychange forces a recalculation
 * on resume.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { v4 as uuid } from 'uuid';
import { db } from '@/lib/db';
import { repository } from '@/lib/DexieRepository';
import { generateWorkout } from '@/lib/WorkoutGenerator';
import { applyDeloadIfNeeded } from '@/lib/deloadDetector';
import { calc1RM } from '@/hooks/use1RM';
import type {
  Exercise,
  LoggedSet,
  MuscleGroup,
  MuscleState,
  GeneratedExercise,
  PersonalRecord,
  SplitDay,
  RPE,
} from '@/types';
import type { GeneratorOutput } from '@/lib/WorkoutGenerator';

// ── Types ────────────────────────────────────────────────────

export type WorkoutPhase = 'idle' | 'generating' | 'preview' | 'active' | 'complete' | 'abandoned';

export interface LogSetParams {
  exerciseId: string;
  setNumber: number;
  weightLbs: number;
  reps: number;
  rpe: RPE;
  isWarmup: boolean;
}

export interface UseWorkoutSessionReturn {
  phase: WorkoutPhase;
  plan: GeneratorOutput | null;
  loggedSets: Map<string, LoggedSet[]>;
  activeWorkoutId: string | null;
  elapsedSeconds: number;
  error: string | null;
  latestPR: PersonalRecord | null;
  sessionPRs: PersonalRecord[];
  clearLatestPR: () => void;
  generate: (splitDayOverride?: SplitDay, durationOverride?: number) => Promise<void>;
  beginWorkout: () => void;
  tryRestore: () => Promise<boolean>;
  logSet: (params: LogSetParams) => Promise<void>;
  deleteSet: (setId: string) => Promise<void>;
  updateSet: (setId: string, patch: Partial<LoggedSet>) => Promise<void>;
  substituteExercise: (index: number, replacement: Exercise) => void;
  addExercise: (exercise: Exercise) => void;
  completeWorkout: (note?: string) => Promise<void>;
  abandonWorkout: () => Promise<void>;
  reset: () => void;
}

// ── Hook ─────────────────────────────────────────────────────

export function useWorkoutSession(): UseWorkoutSessionReturn {
  const [phase, setPhase] = useState<WorkoutPhase>('idle');
  const [plan, setPlan] = useState<GeneratorOutput | null>(null);
  const [activeWorkoutId, setActiveWorkoutId] = useState<string | null>(null);
  const [loggedSets, setLoggedSets] = useState<Map<string, LoggedSet[]>>(new Map());
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [latestPR, setLatestPR] = useState<PersonalRecord | null>(null);
  const [sessionPRs, setSessionPRs] = useState<PersonalRecord[]>([]);

  // Absolute start time — survives background throttling
  const startTimeRef = useRef<number | null>(null);
  const intervalRef  = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Timer ────────────────────────────────────────────────────

  const tick = useCallback(() => {
    if (startTimeRef.current === null) return;
    setElapsedSeconds(Math.floor((Date.now() - startTimeRef.current) / 1000));
  }, []);

  const startTimer = useCallback(() => {
    startTimeRef.current = Date.now();
    intervalRef.current = setInterval(tick, 1000);
  }, [tick]);

  const stopTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Recalculate elapsed time when the tab comes back from background
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && startTimeRef.current !== null) {
        tick(); // immediate recalc, no waiting for next interval
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [tick]);

  // Cleanup timer on unmount
  useEffect(() => () => stopTimer(), [stopTimer]);

  // ── Reset ────────────────────────────────────────────────────

  const clearLatestPR = useCallback(() => setLatestPR(null), []);

  const reset = useCallback(() => {
    stopTimer();
    setPhase('idle');
    setPlan(null);
    setActiveWorkoutId(null);
    setLoggedSets(new Map());
    setElapsedSeconds(0);
    setError(null);
    setLatestPR(null);
    setSessionPRs([]);
    startTimeRef.current = null;
  }, [stopTimer]);

  // ── Generate ─────────────────────────────────────────────────

  const generate = useCallback(async (splitDayOverride?: SplitDay, durationOverride?: number) => {
    setPhase('generating');
    setError(null);

    try {
      const [exercises, muscleStateRows, settings, gymProfile] = await Promise.all([
        db.exercises.toArray(),
        db.muscleStates.toArray(),
        repository.getSettings(),
        repository.getActiveGymProfile(),
      ]);

      if (!settings)    throw new Error('App settings not found — try reloading');
      if (!gymProfile)  throw new Error('No gym profile found — check Settings');

      const muscleStates = new Map<MuscleGroup, MuscleState>(
        muscleStateRows.map((s) => [s.muscle, s]),
      );

      // Rolling 7-day hard-set volume map
      const cutoff = new Date(Date.now() - 7 * 86_400_000).toISOString();
      const recentSets = await db.loggedSets
        .where('loggedAt').aboveOrEqual(cutoff)
        .filter((s) => !s.isWarmup && s.rpe >= 7)
        .toArray();

      const recentExIds = [...new Set(recentSets.map((s) => s.exerciseId))];
      const recentExercises = await db.exercises.bulkGet(recentExIds);
      const recentExMap = new Map(
        (recentExercises.filter(Boolean) as typeof exercises).map((e) => [e.id, e]),
      );

      const weeklyVolume = new Map<MuscleGroup, number>();
      for (const s of recentSets) {
        const ex = recentExMap.get(s.exerciseId);
        if (!ex) continue;
        for (const alloc of ex.muscles) {
          weeklyVolume.set(alloc.muscle, (weeklyVolume.get(alloc.muscle) ?? 0) + alloc.fraction);
        }
      }

      // 30-day exercise history for progressive overload
      const historySets = await db.loggedSets
        .where('loggedAt')
        .aboveOrEqual(new Date(Date.now() - 30 * 86_400_000).toISOString())
        .filter((s) => !s.isWarmup)
        .toArray();

      const exerciseHistory = new Map<string, LoggedSet[]>();
      const usageCount     = new Map<string, number>();
      for (const s of historySets) {
        exerciseHistory.set(s.exerciseId, [...(exerciseHistory.get(s.exerciseId) ?? []), s]);
        usageCount.set(s.exerciseId, (usageCount.get(s.exerciseId) ?? 0) + 1);
      }

      // Auto-clear deload flag now that the session is being generated
      if (settings.deloadRecommended) {
        await repository.updateSettings({ deloadRecommended: false });
      }

      const effectiveSettings = durationOverride
        ? { ...settings, preferredDurationMinutes: durationOverride as import('@/types').PreferredDuration }
        : settings;

      const output = generateWorkout({
        splitDay: splitDayOverride ?? settings.nextSplitDay,
        availableExercises: exercises,
        muscleStates,
        exerciseHistory,
        exerciseUsageCount: usageCount,
        gymProfile,
        settings: effectiveSettings,
        sessionVolume: new Map(),
        weeklyVolume,
      });

      const workoutId = uuid();
      await repository.createWorkout({
        id: workoutId,
        startedAt: new Date().toISOString(),
        splitDay: output.splitDay,
        status: 'in_progress',
        gymProfileId: gymProfile.id,
        planJson: JSON.stringify(output),
      });

      setActiveWorkoutId(workoutId);
      setPlan(output);
      setLoggedSets(new Map());
      setElapsedSeconds(0);
      setLatestPR(null);
      setSessionPRs([]);
      setPhase('preview'); // timer starts only when user taps "Start Workout"
    } catch (err) {
      setError(String(err));
      setPhase('idle');
    }
  }, [startTimer]);

  // ── Begin Workout (preview → active, starts timer) ───────────

  const beginWorkout = useCallback(() => {
    startTimer();
    setPhase('active');
  }, [startTimer]);

  // ── Restore in-progress session on app resume ─────────────────

  const tryRestore = useCallback(async (): Promise<boolean> => {
    try {
      // Find the most recent in-progress session
      const sessions = await db.workouts
        .where('status').equals('in_progress')
        .sortBy('startedAt');
      const session = sessions[sessions.length - 1]; // most recent
      if (!session || !session.planJson) return false;

      let restoredPlan: GeneratorOutput;
      try {
        restoredPlan = JSON.parse(session.planJson) as GeneratorOutput;
      } catch {
        return false; // malformed plan — start fresh
      }

      // Rebuild loggedSets map from DB
      const sets = await db.loggedSets
        .where('workoutId').equals(session.id)
        .toArray();

      const restoredSets = new Map<string, LoggedSet[]>();
      for (const s of sets) {
        restoredSets.set(s.exerciseId, [...(restoredSets.get(s.exerciseId) ?? []), s]);
      }

      // Re-anchor the elapsed timer from the session start
      const startedMs = new Date(session.startedAt).getTime();
      startTimeRef.current = startedMs;
      intervalRef.current = setInterval(tick, 1000);

      setActiveWorkoutId(session.id);
      setPlan(restoredPlan);
      setLoggedSets(restoredSets);
      setElapsedSeconds(Math.floor((Date.now() - startedMs) / 1000));
      setPhase('active');
      return true;
    } catch {
      return false;
    }
  }, [tick]);

  // ── Log Set ──────────────────────────────────────────────────

  const logSet = useCallback(async (params: LogSetParams) => {
    if (!activeWorkoutId) return;

    const newSet: LoggedSet = {
      id: uuid(),
      workoutId: activeWorkoutId,
      exerciseId: params.exerciseId,
      setNumber: params.setNumber,
      weightLbs: params.weightLbs,
      reps: params.reps,
      rpe: params.rpe,
      rir: 10 - params.rpe,
      loggedAt: new Date().toISOString(),
      isWarmup: params.isWarmup,
      estimated1RM: calc1RM(params.weightLbs, params.reps),
    };

    const pr = await repository.logSet(newSet);

    setLoggedSets((prev) => {
      const next = new Map(prev);
      next.set(params.exerciseId, [...(next.get(params.exerciseId) ?? []), newSet]);
      return next;
    });

    if (pr) {
      setLatestPR(pr);
      setSessionPRs((prev) => [...prev, pr]);
    }
  }, [activeWorkoutId]);

  // ── Delete Set ───────────────────────────────────────────────

  const deleteSet = useCallback(async (setId: string) => {
    await repository.deleteSet(setId);
    setLoggedSets((prev) => {
      const next = new Map(prev);
      for (const [exId, sets] of next) {
        next.set(exId, sets.filter((s) => s.id !== setId));
      }
      return next;
    });
  }, []);

  // ── Substitute ───────────────────────────────────────────────

  const substituteExercise = useCallback((index: number, replacement: Exercise) => {
    setPlan((prev) => {
      if (!prev) return prev;
      const exercises = [...prev.exercises];
      const original = exercises[index];
      const newEntry: GeneratedExercise = {
        exercise: replacement,
        warmupSets: original.warmupSets,
        workingSets: original.workingSets,
      };
      exercises[index] = newEntry;
      return { ...prev, exercises };
    });
  }, []);

  // ── Add Exercise ─────────────────────────────────────────────

  const addExercise = useCallback((exercise: Exercise) => {
    setPlan((prev) => {
      if (!prev) return prev;
      // Don't add if already in plan
      if (prev.exercises.some((e) => e.exercise.id === exercise.id)) return prev;
      const newEntry: GeneratedExercise = {
        exercise,
        warmupSets: [],
        workingSets: Array.from({ length: 3 }, (_, i) => ({
          setNumber: i + 1,
          suggestedWeightLbs: 0,
          targetReps: 10,
          targetRPE: 8 as RPE,
          restSeconds: exercise.restSeconds,
        })),
      };
      return {
        ...prev,
        exercises: [...prev.exercises, newEntry],
        totalWorkingSets: prev.totalWorkingSets + 3,
      };
    });
  }, []);

  // ── Update Set ───────────────────────────────────────────────

  const updateSet = useCallback(async (setId: string, patch: Partial<LoggedSet>) => {
    await repository.updateSet(setId, patch);
    setLoggedSets((prev) => {
      const next = new Map(prev);
      for (const [exId, sets] of next) {
        next.set(exId, sets.map((s) => s.id === setId ? { ...s, ...patch } : s));
      }
      return next;
    });
  }, []);

  // ── Complete ─────────────────────────────────────────────────

  const completeWorkout = useCallback(async (note?: string) => {
    if (!activeWorkoutId) return;
    stopTimer();

    await repository.updateWorkout(activeWorkoutId, {
      completedAt: new Date().toISOString(),
      status: 'completed',
      durationSeconds: elapsedSeconds,
      ...(note ? { notes: note } : {}),
    });

    // Advance the A/B split for next session
    await (repository as typeof repository & { advanceSplitDay?: () => Promise<SplitDay> })
      .advanceSplitDay?.();

    // Check if a deload week is needed
    await applyDeloadIfNeeded();

    setPhase('complete');
  }, [activeWorkoutId, elapsedSeconds, stopTimer]);

  // ── Abandon ──────────────────────────────────────────────────

  const abandonWorkout = useCallback(async () => {
    if (!activeWorkoutId) return;
    stopTimer();

    await repository.updateWorkout(activeWorkoutId, {
      completedAt: new Date().toISOString(),
      status: 'abandoned',
      durationSeconds: elapsedSeconds,
    });

    reset();
  }, [activeWorkoutId, elapsedSeconds, stopTimer, reset]);

  return {
    phase, plan, loggedSets, activeWorkoutId,
    elapsedSeconds, error, latestPR, sessionPRs, clearLatestPR,
    generate, beginWorkout, tryRestore, logSet, deleteSet, updateSet, substituteExercise, addExercise,
    completeWorkout, abandonWorkout, reset,
  };
}

// ── Utility ──────────────────────────────────────────────────

export function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}
